<script lang="ts">
  /**
   * Constellation viewport — deck.gl ScatterplotLayer (+ optional LineLayer for
   * edges) rendering pre-projected memory layouts.
   *
   * v0.1 features:
   *   - 2D / 3D camera modes (OrthographicView ↔ OrbitView)
   *   - hover tooltip via Deck.getTooltip
   *   - click → write selection.selectedId (drives the side panel)
   *   - edge layer (relationships) toggle, fetched lazily for visible points
   *   - importance tier + topic-search filtering (client-side)
   *   - top-right stats overlay (count, computed-at, variance explained)
   *   - keyboard: r reset, f fit, e edges, 1-4 projection, esc clear selection
   *
   * The instance is recreated when viewMode flips since deck.gl's `views`
   * prop isn't reliably hot-swappable. All other changes go via setProps.
   */

  import { onMount, untrack } from 'svelte'
  import { Deck, OrthographicView, OrbitView } from '@deck.gl/core'
  import type { PickingInfo } from '@deck.gl/core'
  import { ScatterplotLayer, LineLayer } from '@deck.gl/layers'
  import {
    fetchLayout, fetchEdges, fetchMemoryDetail,
    type LayoutPoint, type Edge,
  } from './api.svelte'
  import {
    selection, filters, overrides, trail, pushTrailStep,
    passesFilter, tierThresholds,
  } from './selection.svelte'
  import { layoutPoints } from './layoutStore.svelte'
  import { reportSelection, notifyHost } from './addonProtocol.svelte'
  import { mco } from './mcoAuth.svelte'

  let canvasEl: HTMLCanvasElement | null = $state(null)
  let deck: Deck<OrthographicView | OrbitView> | null = null

  let allPoints = $state<LayoutPoint[]>([])
  let edges = $state<Edge[]>([])
  let loading = $state(true)
  let computedAt = $state<string | null>(null)
  let varianceExplained = $state<number[] | null>(null)
  let elapsedMs = $state<number | null>(null)

  /* Reactive view: filtered point list driven by filters store + per-id
   * overrides. Z amplification is NOT applied to the data here — it's a
   * render-time concern (see posZ + zPosition below) so points and edges
   * always agree on the same coordinate. */
  const Z_AMP_FACTOR = 1.8
  const visiblePoints = $derived.by(() => {
    void overrides.hidden.size  /* reactivity sentinel */
    return allPoints.filter(
      (p) => passesFilter(p, filters) && !overrides.hidden.has(p.id),
    )
  })

  /* Render-time Z accessor. Amplifies the third axis only when 3D mode +
   * AMP toggle is on, so PCA's small PC3 variance doesn't collapse the
   * z-axis visually. Same function used by ScatterplotLayer.getPosition
   * AND LineLayer.getSourcePosition/getTargetPosition — that's the only
   * way to guarantee points and edge endpoints sit at the same z. (When
   * the previous version of this file amplified z inside visiblePoints
   * but not inside pointById, edges connected at the un-amplified z while
   * dots floated at the amplified z — lines didn't reach the dots.) */
  function posZ(p: LayoutPoint): number {
    const z = p.z ?? 0
    return (filters.viewMode === '3d' && filters.zAmplify) ? z * Z_AMP_FACTOR : z
  }
  function zPosition(p: LayoutPoint): [number, number, number] {
    return [p.x, p.y, posZ(p)]
  }

  /* Edge-endpoint lookup is built from `allPoints` (changes only on layout
   * fetch), not `visiblePoints` (changes on every filter/hide tick). The
   * Map stays stable across hide/unhide so we don't pay the O(n) rebuild
   * for every tier-toggle or search-keystroke. */
  const pointById = $derived.by(() => {
    const m = new Map<number, LayoutPoint>()
    for (const p of allPoints) m.set(p.id, p)
    return m
  })

  /* Visible-id Set for the edge-renderable filter. Cheap O(n) build, no
   * per-item allocation (just integer hashes), runs on filter changes. */
  const visibleIdSet = $derived.by(() => {
    const s = new Set<number>()
    for (const p of visiblePoints) s.add(p.id)
    return s
  })

  /* Stable primitive key combining all triggers that should invalidate
   * deck.gl accessors. Comparing one string is cheaper than comparing a
   * five-element array, and a string literal allocates once vs deck.gl
   * doing element-wise equality on a fresh array every frame. */
  /* NOTE: hoveredId is deliberately NOT in these keys. Hover is drawn by a
   * separate 1-point overlay layer (see hoverLayer), so it must never
   * invalidate the 39K-point scatter accessors — that O(N)-per-mousemove
   * re-color was the full-corpus freeze. */
  const colorTriggerKey = $derived(
    `${selection.selectedId ?? '_'}|${visiblePoints.length}|${overrides.highlighted.size}|${overrides.customColors.size}`,
  )
  const radiusTriggerKey = $derived(
    `${selection.selectedId ?? '_'}|${visiblePoints.length}|${overrides.highlighted.size}`,
  )

  /* Camera state — independent for each mode. */
  let ortho2D = $state<{ target: [number, number, number]; zoom: number }>({
    target: [0, 0, 0],
    zoom: 2,
  })
  let orbit3D = $state<{
    target: [number, number, number]
    zoom: number
    rotationX: number
    rotationOrbit: number
  }>({
    target: [0, 0, 0],
    zoom: 2,
    rotationX: 30,
    rotationOrbit: 0,
  })

  function pointColor(p: LayoutPoint): [number, number, number, number] {
    /* Color priority (top wins):
     *   1. Selection — pure white, opaque
     *   2. Highlighted (user override) — pure amber-bright
     *   3. Custom color (user override) — exact RGB the user picked
     *   4. Default — amber scaled by importance
     * Hover is intentionally absent — it's drawn by the separate hoverLayer
     * overlay so it never re-runs this accessor across all visible points.
     */
    if (selection.selectedId === p.id) return [255, 240, 200, 255]
    if (overrides.highlighted.has(p.id)) return [255, 204, 68, 255]
    const custom = overrides.customColors.get(p.id)
    if (custom) return [custom[0], custom[1], custom[2], 240]
    const i = Math.max(0, Math.min(25, p.importance ?? 5))
    const t = 0.35 + (i / 25) * 0.65
    return [Math.round(255 * t), Math.round(176 * t), 0, 230]
  }

  function pointRadius(p: LayoutPoint): number {
    const i = p.importance ?? 0
    /* World units (radiusUnits='common'): the layout cube spans ~55 units,
     * so 0.3–1.6 keeps points a fraction of a percent of the span. With
     * radiusMin/MaxPixels clamps the circles scale with perspective —
     * nearer = bigger, farther = smaller, like a real constellation. */
    let r = 0.2 + Math.max(0, i) * 0.034
    if (selection.selectedId === p.id) r *= 1.8
    return r
  }

  /* ── Binary attribute buffers ──────────────────────────────────────
   * deck.gl renders on the GPU but BUILDS attribute buffers on the main
   * thread — calling getPosition/getFillColor/getRadius once per point.
   * At 39K points that per-rebuild pass is what locked the UI. Instead we
   * precompute typed arrays here as $derived values: each recomputes only
   * when its real inputs change, and deck.gl uploads them directly with
   * zero per-point accessor calls. */
  const pointPositions = $derived.by(() => {
    const pts = visiblePoints
    const a = new Float32Array(pts.length * 3)
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i]
      a[i * 3] = p.x
      a[i * 3 + 1] = p.y
      a[i * 3 + 2] = posZ(p)
    }
    return a
  })
  const pointColors = $derived.by(() => {
    void colorTriggerKey
    const pts = visiblePoints
    const a = new Uint8Array(pts.length * 4)
    for (let i = 0; i < pts.length; i++) {
      const c = pointColor(pts[i])
      a[i * 4] = c[0]
      a[i * 4 + 1] = c[1]
      a[i * 4 + 2] = c[2]
      a[i * 4 + 3] = c[3]
    }
    return a
  })
  const pointRadii = $derived.by(() => {
    void radiusTriggerKey
    const pts = visiblePoints
    const a = new Float32Array(pts.length)
    for (let i = 0; i < pts.length; i++) a[i] = pointRadius(pts[i])
    return a
  })

  /* Edge binary buffers — same idea as the point buffers, for the edge
   * LineLayer. Filtering to renderable edges + resolving endpoint
   * coordinates happens once here in a plain loop, not via deck.gl
   * accessors invoked per-edge on every rebuild. Recomputes only when
   * edges, the visible set, edge mode, or the 2D/3D projection change. */
  const edgeBuffers = $derived.by(() => {
    const re = edges.filter(
      (e) => visibleIdSet.has(e.source) && visibleIdSet.has(e.target),
    )
    const n = re.length
    const src = new Float32Array(n * 3)
    const tgt = new Float32Array(n * 3)
    const col = new Uint8Array(n * 4)
    const wid = new Float32Array(n)
    const selMode = filters.edgeMode === 'selected'
    const baseA = selMode ? 200 : 90
    const wBase = selMode ? 1.5 : 1.0
    for (let i = 0; i < n; i++) {
      const e = re[i]
      const a = pointById.get(e.source)
      const b = pointById.get(e.target)
      if (!a || !b) continue
      src[i * 3] = a.x; src[i * 3 + 1] = a.y; src[i * 3 + 2] = posZ(a)
      tgt[i * 3] = b.x; tgt[i * 3 + 1] = b.y; tgt[i * 3 + 2] = posZ(b)
      const alpha = Math.max(60, Math.min(255, Math.round(baseA + e.strength * 160)))
      if (selMode) { col[i * 4] = 255; col[i * 4 + 1] = 200; col[i * 4 + 2] = 80 }
      else { col[i * 4] = 176; col[i * 4 + 1] = 112; col[i * 4 + 2] = 0 }
      col[i * 4 + 3] = alpha
      /* World units (widthUnits='common') — edges thin with distance,
       * thicken up close, matching the point perspective. */
      wid[i] = Math.max(0.03, wBase * 0.045 + e.strength * 0.1)
    }
    return { length: n, src, tgt, col, wid }
  })

  function buildLayers() {
    /* Visual layer — renders the points the user sees. Picking is OFF here;
     * the hit layer below handles input so we can satisfy WCAG 2.5.5
     * (Target Size AAA = 44×44 CSS px) without inflating the visual
     * importance-encoded radius. */
    const scatter = new ScatterplotLayer({
      id: 'memory-points',
      /* Binary data — deck.gl uploads these buffers straight to the GPU
       * with no per-point accessor calls. The buffers are $derived, so a
       * layer rebuild that doesn't change point data reuses them as-is. */
      data: {
        length: visiblePoints.length,
        attributes: {
          getPosition: { value: pointPositions, size: 3 },
          getFillColor: { value: pointColors, size: 4 },
          getRadius: { value: pointRadii, size: 1 },
        },
      },
      /* World units → perspective: near points render bigger, far smaller.
       * billboard → each circle always faces the camera. min/max pixel
       * clamps keep far points visible and near points from exploding. */
      radiusUnits: 'common',
      billboard: true,
      radiusMinPixels: 1.5,
      radiusMaxPixels: 40,
      pickable: false,
      antialiasing: true,
    })

    /* WCAG 2.5.5 hit layer — invisible 44px-diameter pick targets layered
     * over the visual points. Uses the same `data` array so picking
     * resolves to the same LayoutPoint object the visual layer would.
     * `getFillColor` returns transparent so it doesn't render; the picking
     * buffer doesn't care about alpha (it uses object IDs, not visible
     * color). Radius is half the AAA minimum (44px / 2 = 22px). */
    const hitLayer = new ScatterplotLayer({
      id: 'memory-points-hit',
      /* Shares the scatter's binary position buffer; only needs position. */
      data: {
        length: visiblePoints.length,
        attributes: {
          getPosition: { value: pointPositions, size: 3 },
        },
      },
      getFillColor: [0, 0, 0, 0],
      getRadius: 22,
      radiusUnits: 'pixels',
      radiusMinPixels: 22,
      pickable: true,
      onClick: (info: PickingInfo) => {
        const idx = info.index
        if (idx != null && idx >= 0 && idx < visiblePoints.length) {
          selection.selectedId = visiblePoints[idx].id
        }
      },
      onHover: (info: PickingInfo) => {
        const idx = info.index
        const p =
          idx != null && idx >= 0 && idx < visiblePoints.length
            ? visiblePoints[idx]
            : null
        selection.hoveredId = p ? p.id : null
        selection.hoveredPoint = p ?? null
      },
    })

    /* Mixed-data layers (scatter + edge LineLayer + trail LineLayer with
     * different row shapes) — deck.gl's generic is invariant, so we use
     * a permissive runtime-typed array here. The `Layer` base type would
     * also work but the imports get noisier.
     *
     * Order matters: layers are drawn AND hit-tested in the order they
     * appear in `layers`. The hit layer goes LAST so its (invisible)
     * 44px disks sit on top of everything for picking, while the visual
     * scatter renders first underneath. Edges/trail are unshifted to the
     * front so they render below the points visually. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const layers: any[] = [scatter]

    /* Trail polyline — bright walk path between consecutive trail steps,
     * but only between segments where both endpoints exist in the current
     * visiblePoints set. Off-screen trail segments silently drop. */
    if (trail.steps.length > 1) {
      const trailSegments: Array<{ a: LayoutPoint; b: LayoutPoint; idx: number }> = []
      for (let i = 1; i < trail.steps.length; i++) {
        const a = pointById.get(trail.steps[i - 1].id)
        const b = pointById.get(trail.steps[i].id)
        if (a && b) trailSegments.push({ a, b, idx: i })
      }
      if (trailSegments.length > 0) {
        const trailLayer = new LineLayer<{ a: LayoutPoint; b: LayoutPoint; idx: number }>({
          id: 'memory-trail',
          data: trailSegments,
          getSourcePosition: (s) => zPosition(s.a),
          getTargetPosition: (s) => zPosition(s.b),
          /* Fade older segments toward soft amber, newest is bright white-amber. */
          getColor: (s) => {
            const fade = Math.max(0.4, s.idx / trail.steps.length)
            return [255, Math.round(220 * fade + 100), Math.round(120 * fade), 220]
          },
          getWidth: (s) => 1.5 + (s.idx / trail.steps.length) * 1.5,
          widthUnits: 'pixels',
          updateTriggers: {
            getColor: [trail.steps.length],
            getWidth: [trail.steps.length],
            getSourcePosition: `${filters.viewMode}|${filters.zAmplify}`,
            getTargetPosition: `${filters.viewMode}|${filters.zAmplify}`,
          },
        })
        layers.unshift(trailLayer)
      }
    }

    if (edgeBuffers.length > 0) {
      /* Binary edge layer — endpoint coordinates, color and width are all
       * precomputed typed arrays (see edgeBuffers). deck.gl uploads them
       * directly; no per-edge accessor pass on rebuild. */
      const lines = new LineLayer({
        id: 'memory-edges',
        data: {
          length: edgeBuffers.length,
          attributes: {
            getSourcePosition: { value: edgeBuffers.src, size: 3 },
            getTargetPosition: { value: edgeBuffers.tgt, size: 3 },
            getColor: { value: edgeBuffers.col, size: 4 },
            getWidth: { value: edgeBuffers.wid, size: 1 },
          },
        },
        /* World units → edges scale with depth like the points. */
        widthUnits: 'common',
        widthMinPixels: 0.5,
        widthMaxPixels: 12,
      })
      layers.unshift(lines) /* edges under points */
    }

    /* Hit layer goes last so it sits on top in the picking buffer. */
    layers.push(hitLayer)

    return layers
  }

  function getTooltip(info: PickingInfo): string | null {
    /* Binary data carries no per-row object — resolve via the picked index. */
    const idx = info.index
    if (idx == null || idx < 0 || idx >= visiblePoints.length) return null
    return formatTooltip(visiblePoints[idx])
  }

  function formatTooltip(p: LayoutPoint): string {
    const tier = tierLabel(p.importance ?? 0)
    const topic = p.topic ?? '(no topic)'
    return `<div class="mu-tt">
      <div class="mu-tt-row mu-tt-id">#${p.id} <span class="mu-tt-tier mu-tt-${tier.toLowerCase()}">${tier}</span></div>
      <div class="mu-tt-row mu-tt-topic">${escapeHtml(topic.slice(0, 64))}</div>
      <div class="mu-tt-row mu-tt-meta">imp ${p.importance ?? 0}${p.agent_id ? ' · ' + escapeHtml(p.agent_id) : ''}</div>
    </div>`
  }
  function tierLabel(n: number): string {
    if (n >= 20) return 'APEX'
    if (n >= 15) return 'ELITE'
    if (n >= 5)  return 'HIGH'
    if (n >= 0)  return 'BASE'
    return 'LOW'
  }
  function escapeHtml(s: string): string {
    return s.replace(/[&<>"']/g, (c) => (
      c === '&' ? '&amp;' :
      c === '<' ? '&lt;'  :
      c === '>' ? '&gt;'  :
      c === '"' ? '&quot;' : '&#39;'
    ))
  }

  // ── Lifecycle ──────────────────────────────────────────────────────

  function makeView(mode: '2d' | '3d') {
    return mode === '2d'
      ? new OrthographicView({ id: 'main', controller: true })
      : new OrbitView({ id: 'main', controller: true, orbitAxis: 'Y', fovy: 50 })
  }

  function initialViewState(mode: '2d' | '3d') {
    return mode === '2d' ? ortho2D : orbit3D
  }

  function createDeck() {
    if (!canvasEl) return
    deck?.finalize()
    deck = new Deck({
      canvas: canvasEl,
      views: makeView(filters.viewMode),
      initialViewState: initialViewState(filters.viewMode),
      /* Controlled-mode loop: when the user drags/zooms, deck.gl emits
       * the new state through this callback. We MUST write it back via
       * setProps({viewState}) for the camera to actually render the
       * dragged position — without that, mouse input was being captured
       * but never reflected on screen (the bug that broke mouse control
       * after we switched to viewState-based animation). */
      onViewStateChange: ({ viewState: vs }) => {
        if (filters.viewMode === '2d') {
          ortho2D = vs as typeof ortho2D
        } else {
          orbit3D = vs as typeof orbit3D
        }
        deck?.setProps({ viewState: vs })
      },
      layers: buildLayers(),
      getTooltip,
      style: { background: 'transparent' },
    })
  }

  async function loadLayout() {
    loading = true
    try {
      const { min: imin, max: imax } = tierThresholds(filters.tier)
      const res = await fetchLayout({
        projection: filters.projection,
        limit: filters.limit,
        importanceMin: imin,
        importanceMax: imax,
      })
      allPoints = res.points
      computedAt = res.computedAt
      varianceExplained = res.varianceExplained ?? null
      elapsedMs = res.elapsedMs ?? null
      /* Publish to shared store so Drawer's Memories tab can read rows
       * without re-fetching. */
      layoutPoints.all = res.points
      layoutPoints.computedAt = res.computedAt
      layoutPoints.varianceExplained = res.varianceExplained ?? null
      layoutPoints.elapsedMs = res.elapsedMs ?? null
      /* Reset cameras when the projection actually changes shape. */
      ortho2D = { target: [0, 0, 0], zoom: 2 }
      orbit3D = { target: [0, 0, 0], zoom: 2, rotationX: 30, rotationOrbit: 0 }
    } catch (err) {
      notifyHost('error', `layout failed: ${(err as Error).message}`)
    } finally {
      loading = false
    }
  }

  /* Last successfully-fetched edge request key. If the next call would
   * produce the same ids+strength+mode, skip the network round-trip and
   * the 250KB JSON.stringify on the body. The $effect at line 519 fires
   * on every visiblePoints.length change — toggle hover/highlight has
   * been re-firing this as well via length-stable membership shifts. */
  let lastEdgeKey: string | null = null

  function buildEdgeKey(ids: number[], strengthMin: number, mode: string, selectedId: number | null): string {
    /* Sort cheap: ids are already in importance-DESC order from the
     * layout fetch, so identical filter+layout produces identical order.
     * If layout re-fetched we'll just take the cache miss — correct. */
    return `${mode}|${selectedId ?? '_'}|${strengthMin}|${ids.length}|${ids.length > 0 ? ids[0] : '_'}|${ids.length > 1 ? ids[ids.length - 1] : '_'}|${ids.length > 2 ? ids[(ids.length / 2) | 0] : '_'}`
  }

  async function loadEdges() {
    /* Branch by edgeMode (mirrors original Mem-Dash):
     *   none     — clear edges, no fetch
     *   selected — pull the selected memory's relationshipsList and
     *              synthesize edges from it (single round-trip via the
     *              already-cached /api/memory/:id endpoint)
     *   elite    — pairs where both endpoints have importance ≥ 15
     *   apex     — pairs where both endpoints have importance ≥ 20
     *   all      — every relationship among visible points (noisy)
     */
    if (filters.edgeMode === 'none') {
      edges = []
      return
    }

    if (filters.edgeMode === 'selected') {
      if (selection.selectedId == null) { edges = []; return }
      const detail = await fetchMemoryDetail(selection.selectedId)
      const list = detail?.relationshipsList ?? []
      const selId = selection.selectedId
      edges = list.map((r): Edge => ({
        source: r.direction === 'out' ? selId : r.connectedId,
        target: r.direction === 'out' ? r.connectedId : selId,
        type: r.type,
        strength: r.strength,
      }))
      return
    }

    if (visiblePoints.length === 0) { edges = []; return }

    /* Pre-filter to the importance-tier subset before sending IDs. Cuts
     * the request size and lets the server's edge query hit a much
     * smaller set without changing the SQL. */
    const subset = visiblePoints.filter((p) => {
      if (filters.edgeMode === 'all') return true
      const i = p.importance ?? 0
      if (filters.edgeMode === 'elite') return i >= 15
      if (filters.edgeMode === 'apex')  return i >= 20
      return false
    })
    if (subset.length === 0) { edges = []; return }
    const ids = subset.map((p) => p.id)

    /* Sampled-checksum cache key. Three-point sampling (first / mid /
     * last + length) catches the vast majority of "did anything change"
     * without paying the cost of a full id-list hash. If two distinct
     * subsets collide on the sample they get the same edges — acceptable
     * for v0 (the user can force-refresh by toggling edge mode). */
    const key = buildEdgeKey(ids, filters.edgeStrengthMin, filters.edgeMode, selection.selectedId)
    if (key === lastEdgeKey) return

    /* 20K cap (mu-api's max). With per-node edge distribution server-side,
     * this spreads a genuine web across the corpus rather than a 4K clump. */
    const next = await fetchEdges(ids, filters.edgeStrengthMin, 20000)
    edges = next
    lastEdgeKey = key
  }

  // ── Effects ────────────────────────────────────────────────────────

  onMount(() => {
    createDeck()
    void loadLayout()

    const onKey = (e: KeyboardEvent) => {
      /* Ignore when typing in inputs. */
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA')) return

      switch (e.key) {
        case 'Escape':
          if (selection.selectedId !== null) selection.selectedId = null
          break
        case 'r':
          ortho2D = { target: [0, 0, 0], zoom: 2 }
          orbit3D = { target: [0, 0, 0], zoom: 2, rotationX: 30, rotationOrbit: 0 }
          deck?.setProps({
            initialViewState: initialViewState(filters.viewMode),
            viewState: initialViewState(filters.viewMode),
          })
          break
        case 'e': {
          /* Cycle edgeMode: none → selected → elite → apex → all → none */
          const order = ['none', 'selected', 'elite', 'apex', 'all'] as const
          const i = order.indexOf(filters.edgeMode)
          filters.edgeMode = order[(i + 1) % order.length]
          break
        }
        /* Beta: only PCA. The 2/3/4 shortcuts for UMAP/t-SNE/force are
         * disabled until the streaming-projection endpoint ships —
         * live-compute projections are too slow at scale. */
        case '1': filters.projection = 'pca';   break
        case '2d':
        case 'd':
          filters.viewMode = filters.viewMode === '2d' ? '3d' : '2d'
          break
      }
    }
    window.addEventListener('keydown', onKey)

    return () => {
      window.removeEventListener('keydown', onKey)
      deck?.finalize()
      deck = null
    }
  })

  /* Re-fetch layout when projection / limit changes (server-side). Tier and
   * search are client-side filters and don't trigger a re-fetch. */
  $effect(() => {
    void filters.projection
    void filters.limit
    untrack(() => { void loadLayout() })
  })

  /* Re-fetch layout when MCO bearer arrives (or changes). Critical for
   * iframe-hosted mu-app: when DC hands us the bearer via postMessage AFTER
   * Constellation already mounted, the initial fetch ran without auth and
   * fell back to demo data. This $effect re-triggers loadLayout once the
   * token lands so the user sees real points without refreshing. Standalone
   * sign-ins benefit too — clicking 'Sign in' after a cold load refetches
   * automatically. */
  $effect(() => {
    void mco.token
    void mco.tenant?.id
    void mco.viewing?.id          // ticket #9: switching to an agent's memory (or back) reloads the constellation
    untrack(() => { void loadLayout() })
  })

  /* Re-create deck when viewMode flips. */
  $effect(() => {
    void filters.viewMode
    untrack(() => { createDeck() })
  })

  /* Hot-swap layers on filter / selection / hover / edge / trail changes.
   *
   * Deferred via queueMicrotask so the fly-to setProps runs first within
   * the same effect tick — that lets deck.gl register the viewState
   * transition before we replace the layer instances. Without this,
   * fast-firing layer rebuilds can interrupt the controller's interp
   * mid-transition, producing the "snap to target instead of fly"
   * symptom we hit during walk mode. */
  /* The hover highlight is a 1-point overlay layer. Rebuilding it is O(1),
   * so hover never pays the 39K-point scatter rebuild that froze the app
   * at full corpus size. */
  function hoverLayer(): ScatterplotLayer<LayoutPoint> | null {
    const hp = selection.hoveredPoint
    if (!hp) return null
    return new ScatterplotLayer<LayoutPoint>({
      id: 'memory-hover',
      data: [hp],
      getPosition: zPosition,
      getFillColor: [255, 220, 100, 255],
      getRadius: pointRadius(hp) * 1.4,
      /* Match the scatter — world units + billboard so the highlight
       * tracks the point's perspective size and faces the camera. */
      radiusUnits: 'common',
      billboard: true,
      radiusMinPixels: 2,
      radiusMaxPixels: 48,
      pickable: false,
      updateTriggers: {
        getPosition: `${filters.viewMode}|${filters.zAmplify}`,
      },
    })
  }

  /* Heavy layers (scatter + hit + edges + trail) — cached so the hover
   * effect can reuse the instances by reference; deck.gl then no-ops them. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let cachedHeavy: any[] = []

  function paint(): void {
    if (!deck) return
    const hl = hoverLayer()
    deck.setProps({ layers: hl ? [...cachedHeavy, hl] : cachedHeavy })
  }

  /* Heavy rebuild — fires on real layer inputs, NOT hover. queueMicrotask
   * defers past the fly-to setProps so deck.gl registers the viewState
   * transition first (otherwise fast rebuilds snap the camera). */
  $effect(() => {
    void visiblePoints.length
    void filters.edgeMode
    void edges.length
    void selection.selectedId
    void trail.steps.length
    queueMicrotask(() => {
      cachedHeavy = buildLayers()
      paint()
    })
  })

  /* Hover repaint — rebuilds ONLY the 1-point overlay; the cached heavy
   * layers are reused by reference, so deck.gl skips them. O(1) per move. */
  $effect(() => {
    void selection.hoveredId
    queueMicrotask(() => { paint() })
  })

  /* Edge fetch reacts to edgeMode + selection + visible points + strength.
   * Selection-mode reloads when a different memory is clicked. */
  $effect(() => {
    void filters.edgeMode
    void filters.edgeStrengthMin
    void visiblePoints.length
    void selection.selectedId
    untrack(() => { void loadEdges() })
  })

  /* ── Unified camera control ───────────────────────────────────────
   *
   * One RAF loop drives EVERYTHING the camera does. Translation (fly)
   * and rotation (orbit) are independent fields on the same view state;
   * each frame computes both and issues a single setProps call.
   *
   * This replaces the previous "deck.gl manages transitions, my orbit
   * RAF nudges rotation" split which produced races: deck.gl's in-flight
   * target-interpolation was being overwritten by the orbit RAF's
   * rotation updates one frame later, and the layer-rebuild on
   * selection-change occasionally interrupted both.
   *
   * Now translation is interpolated by us (cubic ease-out), rotation is
   * applied per-frame, and the same setProps batch carries both. They
   * compose naturally — fly handles target+zoom, orbit handles
   * rotationOrbit/rotationX, neither steps on the other.
   */

  const FLY_DURATION_MS = 1200
  const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3)

  /* Active fly job — null when no fly is in progress. */
  let flyJob: {
    fromTarget: [number, number, number]
    toTarget: [number, number, number]
    fromZoom: number
    toZoom: number
    startTs: number
  } | null = null

  /* Master RAF — runs only when there's actual work (fly in progress
   * OR orbit active). Stops itself when both are idle to avoid burning
   * CPU on a static scene. */
  let cameraRaf = 0
  let tiltStartTs = Date.now()

  function startCameraRaf(): void {
    if (cameraRaf !== 0) return
    cameraRaf = requestAnimationFrame(cameraTick)
  }

  function cameraTick(): void {
    cameraRaf = 0
    if (!deck) return

    const now = Date.now()
    const is3D = filters.viewMode === '3d'
    const orbiting = filters.orbit && is3D

    let next = is3D ? { ...orbit3D } : { ...ortho2D }

    /* ── Fly interpolation (target + zoom only) ──────────────────── */
    if (flyJob) {
      const elapsed = now - flyJob.startTs
      if (elapsed >= FLY_DURATION_MS) {
        next.target = flyJob.toTarget
        next.zoom = flyJob.toZoom
        flyJob = null
      } else {
        const t = easeOutCubic(elapsed / FLY_DURATION_MS)
        next.target = [
          flyJob.fromTarget[0] + (flyJob.toTarget[0] - flyJob.fromTarget[0]) * t,
          flyJob.fromTarget[1] + (flyJob.toTarget[1] - flyJob.fromTarget[1]) * t,
          flyJob.fromTarget[2] + (flyJob.toTarget[2] - flyJob.fromTarget[2]) * t,
        ]
        next.zoom = flyJob.fromZoom + (flyJob.toZoom - flyJob.fromZoom) * t
      }
    }

    /* ── Orbit (rotation only — independent of fly) ──────────────── */
    if (orbiting) {
      const orbit = next as typeof orbit3D
      orbit.rotationOrbit =
        (orbit.rotationOrbit + filters.orbitSpeed * filters.orbitDirection + 360) % 360
      if (filters.orbitAutoTilt) {
        const t = (now - tiltStartTs) / 1000
        orbit.rotationX = Math.max(
          0,
          Math.min(75, filters.orbitPitch + Math.sin(t * Math.PI / 15) * 15),
        )
      }
    }

    /* Commit to mirror + push to deck via CONTROLLED `viewState`
     * (not `initialViewState`). In controlled mode deck.gl never tries
     * to interpolate — it renders exactly what we say each frame. With
     * `initialViewState` the controller would attempt its own transition
     * logic per setProps call, fighting our cubic interpolation and
     * producing the teleport-with-orbit-on symptom. */
    if (is3D) orbit3D = next as typeof orbit3D
    else      ortho2D = next as typeof ortho2D
    deck.setProps({ viewState: next })

    /* Schedule next frame only if there's still work to do. */
    if (flyJob || orbiting) {
      cameraRaf = requestAnimationFrame(cameraTick)
    }
  }

  /* ── Fly trigger: selection change → seed flyJob ──────────────── */
  $effect(() => {
    void selection.selectedId
    untrack(() => {
      if (!deck || selection.selectedId == null) return
      const p = pointById.get(selection.selectedId)
      if (!p) return
      const is3D = filters.viewMode === '3d'
      const cur = is3D ? orbit3D : ortho2D
      flyJob = {
        fromTarget: [...cur.target] as [number, number, number],
        toTarget: zPosition(p),
        fromZoom: cur.zoom,
        toZoom: Math.max(cur.zoom, is3D ? 3 : 3.5),
        startTs: Date.now(),
      }
      startCameraRaf()
    })
  })

  /* ── Orbit toggle: starts the RAF if needed ─────────────────────── */
  $effect(() => {
    void filters.orbit
    void filters.viewMode
    void filters.orbitSpeed
    void filters.orbitDirection
    void filters.orbitAutoTilt
    untrack(() => {
      if (filters.orbit && filters.viewMode === '3d') {
        tiltStartTs = Date.now()  /* reset auto-tilt phase on settings change */
        startCameraRaf()
      }
      /* When orbit goes OFF, the RAF naturally stops on its next tick
       * (the `if (flyJob || orbiting)` guard). No explicit cancel needed. */
    })
  })

  /* ── Pitch slider: apply directly when not orbiting + not auto-tilt. */
  $effect(() => {
    void filters.orbitPitch
    untrack(() => {
      if (!deck || filters.viewMode !== '3d') return
      if (filters.orbit) return  /* RAF handles it */
      if (filters.orbitAutoTilt) return
      orbit3D = { ...orbit3D, rotationX: filters.orbitPitch }
      deck.setProps({ viewState: orbit3D })  /* controlled mode */
    })
  })

  /* ── WALK camera. Every walkIntervalMs, advance the trail by picking
   * the strongest unvisited connection of the current memory. Composes
   * with orbit: walk fires, fly-to snaps target, orbit RAF spins around. */
  let walkTimer: ReturnType<typeof setTimeout> | null = null
  async function walkNext(): Promise<void> {
    if (!filters.walk) return
    if (selection.selectedId == null) return
    const detail = await fetchMemoryDetail(selection.selectedId)
    if (!detail || !detail.relationshipsList || !filters.walk) return
    const visited = new Set(trail.steps.slice(-15).map((s) => s.id))
    const sorted = [...detail.relationshipsList].sort((a, b) => b.strength - a.strength)
    const next = sorted.find((r) => !visited.has(r.connectedId)) ?? sorted[0]
    if (!next) return
    pushTrailStep(next.connectedId, 'follow')
    selection.selectedId = next.connectedId
  }
  $effect(() => {
    void filters.walk
    void filters.walkIntervalMs
    void selection.selectedId
    untrack(() => {
      if (walkTimer) { clearTimeout(walkTimer); walkTimer = null }
      if (!filters.walk) return
      /* Seed the trail with the current selection so the polyline has
       * a starting point. Subsequent walks build off this. */
      if (selection.selectedId != null && trail.steps.length === 0) {
        pushTrailStep(selection.selectedId, 'manual')
      }
      walkTimer = setTimeout(() => { void walkNext() }, filters.walkIntervalMs)
    })
    return () => {
      if (walkTimer) { clearTimeout(walkTimer); walkTimer = null }
    }
  })
</script>

<div class="con-wrap">
  <div class="con-canvas-wrap">
    <canvas bind:this={canvasEl} class="con-canvas"></canvas>
    {#if loading && allPoints.length === 0}
      <div class="con-overlay">
        <span class="con-overlay-text">LOADING CONSTELLATION…</span>
      </div>
    {/if}

    <!-- Stats card, top-right -->
    <div class="con-stats" aria-label="constellation stats">
      <div class="con-stats-row">
        <span class="con-stats-k">proj</span>
        <span class="con-stats-v">{filters.projection.toUpperCase()}</span>
      </div>
      <div class="con-stats-row">
        <span class="con-stats-k">view</span>
        <span class="con-stats-v">{filters.viewMode.toUpperCase()}</span>
      </div>
      <div class="con-stats-row">
        <span class="con-stats-k">visible</span>
        <span class="con-stats-v">{visiblePoints.length.toLocaleString()} / {allPoints.length.toLocaleString()}</span>
      </div>
      {#if filters.edgeMode !== 'none'}
        <div class="con-stats-row">
          <span class="con-stats-k">edges</span>
          <span class="con-stats-v">{edges.length.toLocaleString()} · {filters.edgeMode}</span>
        </div>
      {/if}
      {#if elapsedMs != null}
        <div class="con-stats-row con-stats-dim">
          <span class="con-stats-k">backend</span>
          <span class="con-stats-v">{elapsedMs}ms</span>
        </div>
      {/if}
      {#if varianceExplained && varianceExplained.length > 0}
        <div class="con-stats-row con-stats-dim">
          <span class="con-stats-k">var-exp</span>
          <span class="con-stats-v">
            {varianceExplained.map((v) => (v * 100).toFixed(0) + '%').join(' · ')}
          </span>
        </div>
      {/if}
    </div>

    <!-- Hover summary, bottom-left, lightweight when picker tooltip is enough -->
    {#if selection.hoveredPoint && !selection.selectedId}
      <div class="con-hover" aria-hidden="true">
        <span class="con-hover-id">#{selection.hoveredPoint.id}</span>
        <span class="con-hover-topic">{selection.hoveredPoint.topic ?? '(no topic)'}</span>
      </div>
    {/if}
  </div>
</div>

<!-- Tooltip styling — global because deck.gl injects raw HTML at body level -->
<svelte:head>
  <style>
    .deck-tooltip {
      background: var(--surface) !important;
      border: 1px solid var(--accent-soft) !important;
      color: var(--on-surface) !important;
      font-family: var(--font-mono) !important;
      font-size: 11px !important;
      padding: 0 !important;
      pointer-events: none;
    }
    .mu-tt { padding: 6px 8px; }
    .mu-tt-row { line-height: 1.5; }
    .mu-tt-id { color: var(--on-surface-variant); display: flex; gap: 6px; align-items: baseline; }
    .mu-tt-topic { color: var(--accent-bright); font-weight: 600; }
    .mu-tt-meta { color: var(--on-surface-faint); font-size: 10px; }
    .mu-tt-tier {
      font-size: 9px; padding: 1px 4px;
      border: 1px solid var(--outline-variant);
      letter-spacing: 0.06em;
    }
    .mu-tt-apex  { color: #FFCC44; border-color: #FFCC44; }
    .mu-tt-elite { color: #FFB000; border-color: #B07000; }
    .mu-tt-high  { color: #E8E8E8; border-color: #444; }
    .mu-tt-base  { color: #888; border-color: #2A2A2A; }
    .mu-tt-low   { color: #FF6B6B; border-color: #FF6B6B; }
  </style>
</svelte:head>

<style>
  .con-wrap {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--surface);
    color: var(--on-surface);
  }

  .con-canvas-wrap {
    position: relative;
    flex: 1;
    min-height: 0;
  }
  .con-canvas {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }

  .con-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }
  .con-overlay-text {
    color: var(--accent);
    font-family: var(--font-mono);
    font-size: 12px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    animation: con-pulse 1.6s ease-in-out infinite;
  }
  @keyframes con-pulse {
    0%, 100% { opacity: 0.55 }
    50%      { opacity: 1.0 }
  }

  .con-stats {
    position: absolute;
    top: var(--s-2);
    right: var(--s-2);
    background: rgba(10, 10, 10, 0.85);
    border: 1px solid var(--outline-variant);
    padding: var(--s-2);
    font-family: var(--font-mono);
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    pointer-events: none;
    backdrop-filter: blur(2px);
  }
  .con-stats-row {
    display: flex;
    gap: var(--s-2);
    line-height: 1.5;
  }
  .con-stats-k {
    color: var(--on-surface-faint);
    width: 56px;
  }
  .con-stats-v { color: var(--on-surface); }
  .con-stats-dim .con-stats-v { color: var(--on-surface-variant); }

  .con-hover {
    position: absolute;
    bottom: var(--s-2);
    left: var(--s-2);
    background: rgba(10, 10, 10, 0.85);
    border: 1px solid var(--accent-soft);
    padding: 4px 8px;
    font-family: var(--font-mono);
    font-size: 10px;
    pointer-events: none;
    display: flex;
    gap: var(--s-2);
    backdrop-filter: blur(2px);
  }
  .con-hover-id { color: var(--accent); font-weight: 700; }
  .con-hover-topic {
    color: var(--on-surface);
    max-width: 320px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
